import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FC, HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from '../../helpers/classNames';
import * as styles from './SingleDatePicker.module.css';

const WEEK_DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const CALENDAR_GAP = 8;
const VIEWPORT_PADDING = 8;

interface CalendarCoords {
  top: number;
  left: number;
  minWidth: number;
  maxWidth?: number;
  maxHeight?: number;
}

export interface SingleDatePickerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: Date | null;
  date?: string | null;
  onChange?: (value: Date | null) => void;
  onDateChange?: (value: string | null) => void;
  label?: string;
  placeholder?: string;
  variant?: 'primary' | 'secondary';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  borderRadius?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  disabled?: boolean;
  error?: string | null;
  errorId?: string;
  minDate?: Date;
  maxDate?: Date;
}

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const startOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const isSameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const isDateDisabled = (date: Date, minDate?: Date, maxDate?: Date) => {
  const time = startOfDay(date).getTime();

  if (minDate && time < startOfDay(minDate).getTime()) return true;
  if (maxDate && time > startOfDay(maxDate).getTime()) return true;

  return false;
};

const getCalendarDays = (month: Date) => {
  const firstDay = startOfMonth(month);
  const firstWeekDay = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(
    firstDay.getFullYear(),
    firstDay.getMonth() + 1,
    0
  ).getDate();
  const daysCount = Math.ceil((firstWeekDay + daysInMonth) / 7) * 7;

  return Array.from({ length: daysCount }, (_, index) => {
    return new Date(
      firstDay.getFullYear(),
      firstDay.getMonth(),
      index - firstWeekDay + 1
    );
  });
};

const formatDate = (date: Date) =>
  date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const parseDateLocal = (dateStr?: string | null): Date | null => {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split('-').map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const SingleDatePicker: FC<SingleDatePickerProps> = ({
  value,
  date,
  onChange,
  onDateChange,
  label,
  placeholder = 'Выберите дату',
  variant = 'primary',
  size = 'md',
  borderRadius = 'md',
  disabled = false,
  error = null,
  errorId = 'datepicker-error',
  minDate,
  maxDate,
  className = '',
  ...props
}) => {
  const selectedValue = value !== undefined ? value : parseDateLocal(date);
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<CalendarCoords>({
    top: 0,
    left: 0,
    minWidth: 0,
  });
  const [visibleMonth, setVisibleMonth] = useState(() =>
    startOfMonth(selectedValue ?? new Date())
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const calendar = calendarRef.current;

    if (!trigger || !calendar) return;

    const triggerRect = trigger.getBoundingClientRect();
    const calendarRect = calendar.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxWidth = Math.max(0, viewportWidth - VIEWPORT_PADDING * 2);
    const maxViewportHeight = Math.max(
      0,
      viewportHeight - VIEWPORT_PADDING * 2
    );
    const calendarWidth = Math.min(calendarRect.width, maxWidth);
    const calendarBorderHeight = calendar.offsetHeight - calendar.clientHeight;
    const naturalCalendarHeight = calendar.scrollHeight + calendarBorderHeight;
    const spaceBelow = Math.max(
      0,
      viewportHeight - triggerRect.bottom - CALENDAR_GAP - VIEWPORT_PADDING
    );
    const spaceAbove = Math.max(
      0,
      triggerRect.top - CALENDAR_GAP - VIEWPORT_PADDING
    );
    const shouldOpenAbove =
      spaceBelow < naturalCalendarHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      maxViewportHeight,
      shouldOpenAbove ? spaceAbove : spaceBelow
    );
    const calendarHeight = Math.min(naturalCalendarHeight, maxHeight);
    const desiredTop = shouldOpenAbove
      ? triggerRect.top - CALENDAR_GAP - calendarHeight
      : triggerRect.bottom + CALENDAR_GAP;
    const maxTop = Math.max(
      VIEWPORT_PADDING,
      viewportHeight - VIEWPORT_PADDING - calendarHeight
    );
    const top = Math.min(Math.max(desiredTop, VIEWPORT_PADDING), maxTop);
    const maxLeft = Math.max(
      VIEWPORT_PADDING,
      viewportWidth - VIEWPORT_PADDING - calendarWidth
    );
    const left = Math.min(
      Math.max(triggerRect.left, VIEWPORT_PADDING),
      maxLeft
    );

    setCoords(current => {
      const next = {
        top,
        left,
        minWidth: Math.min(triggerRect.width, calendarRect.width, maxWidth),
        maxWidth,
        maxHeight,
      };

      return current.top === next.top &&
        current.left === next.left &&
        current.minWidth === next.minWidth &&
        current.maxWidth === next.maxWidth &&
        current.maxHeight === next.maxHeight
        ? current
        : next;
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: PointerEvent) => {
      const target = event.target;

      if (!(target instanceof Node)) return;

      if (
        !triggerRef.current?.contains(target) &&
        !calendarRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    updatePosition();
  });

  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  const calendarDays = useMemo(
    () => getCalendarDays(visibleMonth),
    [visibleMonth]
  );

  const selectedDate = selectedValue ? startOfDay(selectedValue) : null;
  const today = startOfDay(new Date());
  const selectedLabel = selectedValue ? formatDate(selectedValue) : placeholder;
  const monthLabel = visibleMonth.toLocaleDateString('ru-RU', {
    month: 'long',
    year: 'numeric',
  });

  const handleOpen = () => {
    if (disabled) return;

    if (!isOpen && selectedValue) {
      setVisibleMonth(startOfMonth(selectedValue));
    }

    setIsOpen(prev => !prev);
  };

  const handleMonthChange = (offset: number) => {
    setVisibleMonth(
      prev => new Date(prev.getFullYear(), prev.getMonth() + offset, 1)
    );
  };

  const handleSelect = (date: Date) => {
    if (isDateDisabled(date, minDate, maxDate)) return;

    const nextValue = startOfDay(date);
    onChange?.(nextValue);
    onDateChange?.(formatDateLocal(nextValue));
    setVisibleMonth(startOfMonth(nextValue));
    setIsOpen(false);
  };

  const calendar = (
    <div
      className={classNames(styles.calendar, {}, [
        styles[`calendar_${variant}`],
        styles[`calendar_size_${size}`],
        styles[`calendar_border_${borderRadius}`],
      ])}
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        minWidth: coords.minWidth,
        maxWidth: coords.maxWidth,
        maxHeight: coords.maxHeight,
        zIndex: 9999,
      }}
      ref={calendarRef}
      role="dialog"
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.navButton}
          onClick={() => handleMonthChange(-1)}
          aria-label="Предыдущий месяц"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <span className={styles.month}>{monthLabel}</span>
        <button
          type="button"
          className={styles.navButton}
          onClick={() => handleMonthChange(1)}
          aria-label="Следующий месяц"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className={styles.weekDays}>
        {WEEK_DAYS.map(day => (
          <span key={day}>{day}</span>
        ))}
      </div>

      <div className={styles.days}>
        {calendarDays.map(day => {
          const isOutside = day.getMonth() !== visibleMonth.getMonth();
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isToday = isSameDay(day, today);
          const dayDisabled = isDateDisabled(day, minDate, maxDate);

          return (
            <button
              type="button"
              key={day.getTime()}
              className={classNames(
                styles.day,
                {
                  [styles.day_outside]: isOutside,
                  [styles.day_selected]: Boolean(isSelected),
                  [styles.day_today]: isToday,
                },
                []
              )}
              disabled={dayDisabled}
              aria-selected={Boolean(isSelected)}
              onClick={() => handleSelect(day)}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={classNames(styles.root, {}, [className])} {...props}>
      {label && (
        <label
          className={classNames(
            styles.label,
            {
              [styles.label_disabled]: disabled,
              [styles.label_error]: Boolean(error),
            },
            [styles[`label_${variant}`], styles[`label_size_${size}`]]
          )}
        >
          {label}
        </label>
      )}

      <button
        ref={triggerRef}
        type="button"
        className={classNames(
          styles.trigger,
          {
            [styles.trigger_error]: Boolean(error),
            [styles.trigger_open]: isOpen,
          },
          [
            styles[`trigger_${variant}`],
            styles[`trigger_size_${size}`],
            styles[`trigger_border_${borderRadius}`],
          ]
        )}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        onClick={handleOpen}
      >
        <span
          className={classNames(
            styles.value,
            { [styles.value_placeholder]: !selectedValue },
            []
          )}
        >
          {selectedLabel}
        </span>
        <span className={styles.icon} aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 2V5M17 2V5M4 9H20M6 4H18C19.1046 4 20 4.89543 20 6V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {isOpen && createPortal(calendar, document.body)}

      {error && (
        <span
          className={classNames(styles.error, {}, [
            styles[`error_size_${size}`],
          ])}
          id={errorId}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default SingleDatePicker;
